import {
	DROPOFF_SHIPPING_METHODS,
	DROPOFF_TYPES,
	type Dropoff,
	type DropoffShippingMethod,
	type DropoffType,
} from "../types/Dropoff";

/** Which master dropoffs the Scheduling map shows: one checked set per dimension, AND-ed. */
export interface DropoffFilter {
	readonly shippingMethods: ReadonlySet<DropoffShippingMethod>;
	readonly types: ReadonlySet<DropoffType>;
}

/** Everything checked — the session default. */
export const ALL_DROPOFF_FILTER: DropoffFilter = {
	shippingMethods: new Set(DROPOFF_SHIPPING_METHODS),
	types: new Set(DROPOFF_TYPES),
};

/** A dimension the user hasn't narrowed passes everything, including a blank value (a location
 *  Airtable hasn't typed yet). Once narrowed, only checked values pass — an untyped pin must not
 *  linger behind "UPS only". Blanks are a test-double concern in practice: {UPS vs Other} is a
 *  formula, and as of 2026-09 all 641 active dropoffs carry a {Type} (344 Shipping Center /
 *  140 UPS Access Point / 137 Custom Drop / 20 Lab). Revisit with an "Unspecified" checkbox if
 *  untyped active dropoffs ever become common. */
function passes<T extends string>(checked: ReadonlySet<T>, all: readonly T[], value: T | "" | undefined): boolean {
	if (checked.size === all.length) return true;
	return value !== undefined && value !== "" && checked.has(value);
}

export function matchesDropoffFilter(dropoff: Dropoff, filter: DropoffFilter): boolean {
	return (
		passes(filter.shippingMethods, DROPOFF_SHIPPING_METHODS, dropoff.shippingMethod) &&
		passes(filter.types, DROPOFF_TYPES, dropoff.type)
	);
}

function toggled<T>(set: ReadonlySet<T>, value: T): ReadonlySet<T> {
	const next = new Set(set);
	if (next.has(value)) next.delete(value);
	else next.add(value);
	return next;
}

export function toggleDropoffShippingMethod(filter: DropoffFilter, method: DropoffShippingMethod): DropoffFilter {
	return { ...filter, shippingMethods: toggled(filter.shippingMethods, method) };
}

export function toggleDropoffType(filter: DropoffFilter, type: DropoffType): DropoffFilter {
	return { ...filter, types: toggled(filter.types, type) };
}

function widened<T extends string>(
	checked: ReadonlySet<T>,
	all: readonly T[],
	value: T | "" | undefined,
): ReadonlySet<T> {
	if (passes(checked, all, value)) return checked;
	// A blank value can only pass an untouched dimension — reset it.
	if (value === undefined || value === "") return new Set(all);
	return new Set(checked).add(value);
}

/** The smallest widening of `filter` that lets `dropoff` pass — so a dropoff the user acted on
 *  (e.g. selected from a scheduled stop's popup) never sits invisibly behind the filter. Returns
 *  the same filter object when it already passes. */
export function widenDropoffFilterFor(filter: DropoffFilter, dropoff: Dropoff): DropoffFilter {
	if (matchesDropoffFilter(dropoff, filter)) return filter;
	return {
		shippingMethods: widened(filter.shippingMethods, DROPOFF_SHIPPING_METHODS, dropoff.shippingMethod),
		types: widened(filter.types, DROPOFF_TYPES, dropoff.type),
	};
}
